/**
 * Keystone Product Options: client-side text/DOM patches for things their
 * own settings don't (yet) expose - see assets/migration-overrides.css's
 * "STOPGAP" comment above the .key_* rules for the styling side of this
 * same effort. All of this is delete-when-fixed-upstream, same as that
 * CSS block.
 *
 * Runs via MutationObserver, not a one-time scan on page load, because
 * Keystone re-renders this content dynamically as the customer toggles
 * options/dropdowns (the price summary this file mostly deals with only
 * exists in the DOM at all once an option is selected) - a single pass
 * would only ever catch whatever happened to be on screen at that
 * instant.
 *
 * 1. Strips the literal " USD" suffix from Keystone's price text. Root
 *    cause: this store's own Shopify Admin currency format (Settings >
 *    General > Store defaults) has "USD" baked directly into the
 *    formatted price STRING Keystone receives - the exact same root
 *    cause already fixed for the theme's own native price display
 *    elsewhere (snippets/price.liquid, in the main draft theme). The
 *    user did set Keystone's own "hide currency code" option and it's
 *    still showing - worth reporting to Keystone support as a possible
 *    bug on their end, since their toggle apparently isn't preventing
 *    this. This is a client-side patch in the meantime, not a real fix.
 * 2. Renames the "Total Extras" summary heading to "Options Summary" per
 *    request - not a Keystone setting (their config doesn't expose UI
 *    text), so patched via direct DOM text replacement rather than a
 *    CSS content: trick, so screen readers announce the real updated
 *    text, not the original.
 * 3. Tags the price-summary container (identified structurally, not by
 *    class - it's the ancestor of the "Total Extras"/"Base Product
 *    Price"/"Total Price" text that carries Keystone's own inline
 *    background-color style) with a stable class so CSS can style it -
 *    Keystone renders this specific sub-section with inline styles and
 *    no class of its own to hook into directly.
 * 4. Hides the whole .product-options-box (including our own "Additional
 *    Options" heading/border) until Keystone has actually finished
 *    rendering for this product, so the customer never sees an empty
 *    box flash and then disappear. Confirmed live (e.g. the 60:1
 *    electric tarp gear motor product): Keystone renders a
 *    #key_options_block mount point on EVERY product that has the app
 *    block at all, regardless of whether that specific product has any
 *    options actually configured in Keystone's admin - when it doesn't,
 *    #key_options_block ends up with zero children (confirmed live:
 *    childCount 0, offsetParent null) and Keystone itself correctly
 *    renders nothing visible, but our own wrapper markup (the
 *    bordered/grey box + "Additional Options" heading, added in
 *    sections/main-product.liquid) is static Liquid unaware of that, so
 *    it renders regardless.
 *
 *    Settled/empty detection is unchanged from the previous, reactive
 *    version of this file: wait until Keystone's own loading skeleton
 *    (.key-options-skeleton) is gone (still-loading state, don't decide
 *    yet) and then check whether #key_options_block has any element
 *    children at all. Keystone never removes the skeleton element from
 *    the DOM - confirmed live it stays put forever. Its visibility is
 *    driven by a stylesheet rule targeting #key_options_block, not the
 *    skeleton itself: `#key_options_block:not(:empty) + .key-options-skeleton,
 *    #key_options_block[data-options-loaded] + .key-options-skeleton {
 *    display: none }` (confirmed live via document.styleSheets - no
 *    inline style, no class change on the skeleton element at all). So
 *    the "done loading" signal to key off of is that same
 *    data-options-loaded attribute on #key_options_block, checked
 *    directly rather than re-deriving it from the skeleton's computed
 *    style.
 *
 *    WHAT'S NEW vs. the reactive-only version this replaces: the box now
 *    starts hidden by default (see migration-overrides.css -
 *    .product-options-box has no display rule of its own until
 *    .product-options-box--ready is added) instead of starting visible
 *    and only hiding once confirmed empty - that's what actually caused
 *    the visible "flash of empty box" the user originally reported.
 *    updateBoxVisibility() below adds --ready the first time the box
 *    settles (loading OR has content), then keeps toggling --empty
 *    exactly like the old hideBoxIfEmpty() did - so a later variant
 *    switch to a genuinely empty option set still hides it again, same
 *    as before. Two safety nets specifically added because an earlier,
 *    never-committed attempt at this exact change was linked to a real
 *    page freeze (never conclusively root-caused - that version's code
 *    is gone, this isn't a patch on top of it, it's a clean rebuild):
 *      - readyTimer: if Keystone never settles within READY_TIMEOUT_MS,
 *        reveal the box anyway. A late-appearing box is a far smaller
 *        problem than one stuck invisible forever.
 *      - runaway breaker (see scheduleReconcile/runReconcile): a box
 *        being re-patched far more often than any real Keystone render
 *        should require is a sign of a feedback loop (one of our own
 *        DOM writes re-triggering the very observer watching for it),
 *        not normal rendering - trips a hard stop that disconnects that
 *        box's observer and force-reveals it, rather than ever letting
 *        the tab hang. See its comment for specifics.
 * 5. Adds a visible red asterisk to standalone required checkboxes (e.g.
 *    "Confirm Measurements" on custom mesh tarp products) whose asterisk
 *    Keystone otherwise renders somewhere the customer never sees.
 *    Root cause, confirmed live via full outerHTML inspection: Keystone
 *    renders the red " *" marker (plus a screen-reader-only " (required)"
 *    span) ONLY inside each option's group-title element
 *    (`.key_option_title`, id `label_<input-name>`). For a dropdown that
 *    title is the visible field label, so the asterisk shows fine (e.g.
 *    "Tarp Style *"). For a single standalone required checkbox, Keystone
 *    has no separate visible group heading to put above the one checkbox
 *    - correctly, it doesn't render one - but it marks that now-redundant
 *    title `key_sr_only` (confirmed: 1x1px, clip-rect(0,0,0,0), genuinely
 *    screen-reader-only) rather than dropping the whole element, and the
 *    checkbox's own VISIBLE label (`.key_checkbox_text`, e.g. "Confirm
 *    Measurements") is separate markup that never got given an asterisk
 *    of its own. Net effect: the field is fully required (input has the
 *    `required` attribute, screen readers do announce it, validation
 *    does fire on submit) but sighted users get no visual cue until they
 *    try to add to cart and see the error. Patch: for every
 *    `.key_selection_input[required]` whose matching group title
 *    (`#label_<name>`) is `key_sr_only`, append a matching red asterisk
 *    onto the checkbox's own visible text span instead.
 */
(function () {
  var READY_TIMEOUT_MS = 5000;
  // If a single box's patch routine runs more than this many times within
  // RUNAWAY_WINDOW_MS, something is generating mutations faster than any
  // real Keystone render should - stop touching that box's DOM rather than
  // risk a hang. 60 runs/2s is generous headroom over what a normal
  // multi-option product's initial render produces (checked live against
  // this store's most option-heavy products), while still tripping fast
  // if a feedback loop starts.
  var RUNAWAY_LIMIT = 60;
  var RUNAWAY_WINDOW_MS = 2000;

  function patchMissingCheckboxAsterisks(root) {
    var requiredInputs = root.querySelectorAll('.key_selection_input[required]');
    requiredInputs.forEach(function (input) {
      if (!input.name) return;
      var groupTitle = root.querySelector('#label_' + CSS.escape(input.name));
      // Group title is visible (a real dropdown/option-group heading) -
      // it already carries its own asterisk, nothing to patch.
      if (!groupTitle || !groupTitle.classList.contains('key_sr_only')) return;
      var label = input.closest('label');
      var textSpan = label && label.querySelector('.key_checkbox_text, .key_selection_text');
      if (!textSpan || textSpan.querySelector('.key-required-asterisk-patch')) return;
      var asterisk = document.createElement('span');
      asterisk.className = 'key-required-asterisk-patch';
      asterisk.setAttribute('aria-hidden', 'true');
      asterisk.style.color = 'rgb(239, 68, 68)';
      asterisk.textContent = ' *';
      textSpan.appendChild(asterisk);
    });
  }

  // Reads current state only - no DOM writes here, so it's safe to call as
  // often as needed without itself feeding the observer loop.
  function readBoxState(box) {
    var kob = box.querySelector('#key_options_block');
    if (!kob) return { settled: false, empty: true };
    var settled = kob.hasAttribute('data-options-loaded') || kob.children.length > 0;
    return { settled: settled, empty: kob.children.length === 0 };
  }

  // Replaces the old hideBoxIfEmpty(). Starts hidden (see
  // .product-options-box in migration-overrides.css - no display rule
  // until --ready is added) and only reveals once Keystone has actually
  // settled, so the customer never sees an empty box appear and vanish.
  // Once ready, keeps reacting to emptiness the same way the old
  // reactive-only version did (e.g. switching to a no-options variant
  // hides it again).
  function updateBoxVisibility(box, boxState) {
    var st = readBoxState(box);
    if (!st.settled) return;
    if (!box.classList.contains('product-options-box--ready')) {
      box.classList.add('product-options-box--ready');
      if (boxState.readyTimer) {
        clearTimeout(boxState.readyTimer);
        boxState.readyTimer = null;
      }
    }
    box.classList.toggle('product-options-box--empty', st.empty);
  }

  function patchPriceText(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      if (/\bUSD\b/.test(node.textContent)) {
        node.textContent = node.textContent.replace(/\s*\bUSD\b/g, '');
      }
      if (node.textContent.trim() === 'Total Extras') {
        node.textContent = node.textContent.replace('Total Extras', 'Options Summary');
      }
    }
  }

  function tagPriceSummaryBox(root) {
    var candidates = root.querySelectorAll('div[style*="background-color"]');
    candidates.forEach(function (el) {
      if (el.classList.contains('key-price-summary-box')) return;
      var text = el.textContent;
      if (
        text.includes('Total Extras') ||
        text.includes('Options Summary') ||
        text.includes('Base Product Price') ||
        text.includes('Total Price')
      ) {
        el.classList.add('key-price-summary-box');
      }
    });
  }

  function patchAll(box, boxState) {
    patchPriceText(box);
    tagPriceSummaryBox(box);
    updateBoxVisibility(box, boxState);
    patchMissingCheckboxAsterisks(box);
  }

  // Per-box scheduling/circuit-breaker state.
  var boxStates = new WeakMap();

  function getBoxState(box) {
    var s = boxStates.get(box);
    if (!s) {
      s = {
        timer: null,
        pendingSince: null,
        runCount: 0,
        windowStart: Date.now(),
        tripped: false,
        readyTimer: null,
        observer: null,
      };
      boxStates.set(box, s);
    }
    return s;
  }

  function tripBreaker(box, boxState) {
    boxState.tripped = true;
    console.error(
      '[keystone-options-overrides] Bailing out on a .product-options-box: it was ' +
        're-patched ' + boxState.runCount + ' times within ' + RUNAWAY_WINDOW_MS +
        'ms, which points to a feedback loop rather than normal Keystone rendering. ' +
        'Disconnecting the observer for this box and leaving it visible as-is so the ' +
        'page never hangs - price-text/price-summary-styling/required-asterisk patches ' +
        'may be incomplete for it from this point on. Please report this if seen.'
    );
    if (boxState.observer) boxState.observer.disconnect();
    if (boxState.readyTimer) {
      clearTimeout(boxState.readyTimer);
      boxState.readyTimer = null;
    }
    if (boxState.timer) {
      clearTimeout(boxState.timer);
      boxState.timer = null;
    }
    box.classList.add('product-options-box--ready');
    box.classList.remove('product-options-box--empty');
  }

  function runReconcile(box, boxState) {
    if (boxState.tripped) return;

    var now = Date.now();
    if (now - boxState.windowStart > RUNAWAY_WINDOW_MS) {
      boxState.windowStart = now;
      boxState.runCount = 0;
    }
    boxState.runCount += 1;
    if (boxState.runCount > RUNAWAY_LIMIT) {
      tripBreaker(box, boxState);
      return;
    }

    patchAll(box, boxState);
  }

  // Debounces mutation bursts into a single reconcile pass, with a max
  // wait so it can't starve if mutations keep arriving.
  //
  // A plain "wait until mutations stop" debounce isn't enough on its own:
  // the native MutationObserver API already coalesces everything that
  // happens within one synchronous block of JS into a single callback, so
  // that part's free regardless. The real risk is Keystone inserting
  // option rows one at a time ACROSS separate ticks (e.g. one per
  // animation frame, or awaiting something between rows) while it builds
  // out a large options set - each staggered insertion would otherwise be
  // its own callback, each triggering a full DOM scan (TreeWalker over
  // every text node, a querySelectorAll pass) over an ever-growing tree -
  // O(insertions x tree size) work for what's really one logical render.
  // RECONCILE_DEBOUNCE_MS coalesces those staggered ticks into far fewer
  // reconcile passes. RECONCILE_MAX_WAIT_MS is the ceiling on how stale
  // the box is ever allowed to sit if insertions kept arriving faster
  // than the debounce window - guarantees a reconcile fires at least that
  // often even under continuous mutation pressure, so a chatty widget
  // can't indefinitely delay the box ever revealing.
  var RECONCILE_DEBOUNCE_MS = 50;
  var RECONCILE_MAX_WAIT_MS = 300;

  function scheduleReconcile(box, boxState) {
    if (boxState.tripped) return;
    var now = Date.now();
    if (!boxState.pendingSince) boxState.pendingSince = now;
    if (boxState.timer) clearTimeout(boxState.timer);

    var waited = now - boxState.pendingSince;
    var delay = Math.min(RECONCILE_DEBOUNCE_MS, Math.max(0, RECONCILE_MAX_WAIT_MS - waited));
    boxState.timer = setTimeout(function () {
      boxState.timer = null;
      boxState.pendingSince = null;
      runReconcile(box, boxState);
    }, delay);
  }

  function initBox(box) {
    var boxState = getBoxState(box);
    if (boxState.observer) return; // already initialized

    // Fail-safe: never leave the box permanently hidden. If Keystone
    // hasn't signaled done-loading within READY_TIMEOUT_MS (script
    // failure, slow network, an unexpected markup change on their end),
    // show it anyway.
    boxState.readyTimer = setTimeout(function () {
      if (boxState.tripped) return;
      box.classList.add('product-options-box--ready');
    }, READY_TIMEOUT_MS);

    var observer = new MutationObserver(function () {
      scheduleReconcile(box, boxState);
    });
    observer.observe(box, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-options-loaded', 'required'],
    });
    boxState.observer = observer;

    scheduleReconcile(box, boxState);
  }

  document.querySelectorAll('.product-options-box').forEach(initBox);
})();
